import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

import { Modal } from "components/Modal/Modal";
import { PrimaryButton } from "components/Button/PrimaryButton";
import {
  SubscriptionPlan,
  BillingFrequency,
  PaymentMethod,
  useStartCheckout,
  BillingDetails,
} from "hooks/useStripe";
import { useAccount } from "pages/Settings/hooks/useAccount";

interface Props {
  show: boolean;
  plan: SubscriptionPlan;
  frequency: BillingFrequency;
  onClose: () => void;
}

const planLabels: Record<SubscriptionPlan, string> = {
  [SubscriptionPlan.Free]: "Free",
  [SubscriptionPlan.ProMd]: "Dienstleister (Pro MD)",
  [SubscriptionPlan.ProLg]: "Mittelständer (Pro LG)",
};

const frequencyLabels: Record<BillingFrequency, string> = {
  [BillingFrequency.Month]: "Monatlich",
  [BillingFrequency.Year]: "Jährlich",
};

export const InvoiceCheckoutModal = ({
  show,
  plan,
  frequency,
  onClose,
}: Props) => {
  const { t } = useTranslation();
  const { account } = useAccount();
  const { startCheckout, isLoading } = useStartCheckout();

  const [company, setCompany] = useState("");
  const [street, setStreet] = useState("");
  const [postCode, setPostCode] = useState("");
  const [city, setCity] = useState("");
  const [country, setCountry] = useState("");
  const [vatId, setVatId] = useState("");

  useEffect(() => {
    if (account) {
      setCompany((account as any).company || "");
      setStreet(account.street_address || "");
      setPostCode(account.post_code || "");
      setCity(account.city || "");
      setCountry(account.country || "");
    }
  }, [account]);

  const isFormValid =
    company.trim() && street.trim() && postCode.trim() && city.trim() && country.trim();

  const handleConfirm = () => {
    const billing: BillingDetails = {
      companyName: company.trim(),
      street: street.trim(),
      city: city.trim(),
      postalCode: postCode.trim(),
      country: country.trim(),
      ...(vatId.trim() && { vatId: vatId.trim() }),
    };

    startCheckout(
      { plan, frequency, paymentMethod: PaymentMethod.Invoice, billing },
      { onSuccess: () => onClose() }
    );
  };

  return (
    <Modal
      show={show}
      onClose={onClose}
      width="1/2"
      content={
        <div className="space-y-6">
          <div>
            <h3 className="text-lg font-medium text-gray-900">
              {t("settings.billing.invoice_confirm_title")}
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              {t("settings.billing.invoice_confirm_description")}
            </p>
          </div>

          <div className="rounded-md bg-gray-50 p-4 ring-1 ring-inset ring-gray-200">
            <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
              <dt className="font-medium text-gray-500">
                {t("settings.billing.plan_label")}
              </dt>
              <dd className="text-gray-900">{planLabels[plan]}</dd>
              <dt className="font-medium text-gray-500">
                {t("settings.billing.frequency_label")}
              </dt>
              <dd className="text-gray-900">{frequencyLabels[frequency]}</dd>
              <dt className="font-medium text-gray-500">
                {t("settings.billing.pm_label")}
              </dt>
              <dd className="text-gray-900">
                {t("settings.billing.pm_invoice")}
              </dd>
            </dl>
          </div>

          <div>
            <h4 className="text-sm font-medium text-gray-900 mb-3">
              {t("settings.billing.invoice_billing_details")}
            </h4>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <label
                  htmlFor="invoice-company"
                  className="block text-sm font-medium text-gray-700"
                >
                  {t("settings.billing.invoice_company")}
                </label>
                <input
                  id="invoice-company"
                  type="text"
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  placeholder="Hersteller GmbH"
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-cyan-500 focus:ring-cyan-500 sm:text-sm"
                />
              </div>
              <div className="sm:col-span-2">
                <label
                  htmlFor="invoice-street"
                  className="block text-sm font-medium text-gray-700"
                >
                  {t("settings.billing.invoice_street")}
                </label>
                <input
                  id="invoice-street"
                  type="text"
                  value={street}
                  onChange={(e) => setStreet(e.target.value)}
                  placeholder="Musterstraße 1"
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-cyan-500 focus:ring-cyan-500 sm:text-sm"
                />
              </div>
              <div>
                <label
                  htmlFor="invoice-postcode"
                  className="block text-sm font-medium text-gray-700"
                >
                  {t("settings.billing.invoice_postcode")}
                </label>
                <input
                  id="invoice-postcode"
                  type="text"
                  value={postCode}
                  onChange={(e) => setPostCode(e.target.value)}
                  placeholder="12345"
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-cyan-500 focus:ring-cyan-500 sm:text-sm"
                />
              </div>
              <div>
                <label
                  htmlFor="invoice-city"
                  className="block text-sm font-medium text-gray-700"
                >
                  {t("settings.billing.invoice_city")}
                </label>
                <input
                  id="invoice-city"
                  type="text"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  placeholder="Berlin"
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-cyan-500 focus:ring-cyan-500 sm:text-sm"
                />
              </div>
              <div>
                <label
                  htmlFor="invoice-country"
                  className="block text-sm font-medium text-gray-700"
                >
                  {t("settings.billing.invoice_country")}
                </label>
                <input
                  id="invoice-country"
                  type="text"
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                  placeholder="DE"
                  maxLength={2}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-cyan-500 focus:ring-cyan-500 sm:text-sm uppercase"
                />
                <p className="mt-1 text-xs text-gray-400">
                  {t("settings.billing.invoice_country_hint")}
                </p>
              </div>
              <div>
                <label
                  htmlFor="invoice-vat"
                  className="block text-sm font-medium text-gray-700"
                >
                  {t("settings.billing.invoice_vat")}
                </label>
                <input
                  id="invoice-vat"
                  type="text"
                  value={vatId}
                  onChange={(e) => setVatId(e.target.value)}
                  placeholder="DE123456789"
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-cyan-500 focus:ring-cyan-500 sm:text-sm"
                />
                <p className="mt-1 text-xs text-gray-400">
                  {t("settings.billing.invoice_vat_hint")}
                </p>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
            >
              {t("settings.billing.invoice_cancel")}
            </button>
            <PrimaryButton
              onClick={handleConfirm}
              disabled={!isFormValid || isLoading}
            >
              {isLoading ? "..." : t("settings.billing.invoice_submit")}
            </PrimaryButton>
          </div>
        </div>
      }
    />
  );
};
